import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import vendorApi from '../services/vendorApi';

const VendorPayouts = () => {
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPayouts();
  }, []);

  const loadPayouts = async () => {
    try {
      const data = await vendorApi.getPayouts();
      setPayouts(data);
    } catch (err) {
      setError('Failed to load payouts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      calculated: 'info',
      approved: 'primary',
      paid: 'success',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const totalPaid = payouts
    .filter(p => p.status === 'paid')
    .reduce((sum, p) => sum + (p.total_amount || 0), 0);

  const totalPending = payouts
    .filter(p => ['pending', 'approved'].includes(p.status))
    .reduce((sum, p) => sum + (p.total_amount || 0), 0);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Payout History
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        View all your payouts and their status
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Paid
              </Typography>
              <Typography variant="h4" color="success.main">
                ${totalPaid.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Pending Payouts
              </Typography>
              <Typography variant="h4" color="warning.main">
                ${totalPending.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          {payouts.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Payout Date</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell>Payment Method</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payouts.map((payout) => (
                    <TableRow
                      key={payout.id}
                      hover
                      onClick={() => navigate(`/vendor/payouts/${payout.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        {new Date(payout.payout_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{payout.items_count}</TableCell>
                      <TableCell>{payout.payment_method}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {payout.reference_number || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payout.status}
                          color={getStatusColor(payout.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="medium">
                          ${payout.total_amount?.toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              No payouts found
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VendorPayouts;
